import { z } from "zod";
import { Printable } from "./feedback.ts";
import { GroupId, MAILING_LIST_MEMBERS_MAX } from "./groups.ts";
import { UserId } from "./user.ts";

/**
 * Рассылка: объявление преподавателя, квитанция, нитка ответов.
 *
 * Отделено от `./groups.ts` 29.08.2026: группа это именованный список учеников,
 * рассылка это сообщения, и вместе они не помещались в один читаемый файл. Связь
 * односторонняя (здесь читается `GroupId` и потолок рассылочного списка), потому
 * что `groupId` у объявления это ПОМЕТКА «как собрали список», а не владелец:
 * истина это список получателей (раздел 8 `docs/group-lessons-design.md`).
 *
 * ⚠️ **Каким именем ученик видит другого ученика, решает ОДНО место сборки на
 * сервере** (`apps/api/src/repo/announcement-visibility.ts`), а не эти схемы.
 * Правило простое: имя внутри группы, которое задал преподаватель, а если он
 * его не задал, то профильное (`memberNameShown` в `./groups.ts`, решение
 * владельца 29.08.2026).
 *
 * ⚠️ **До 29.08.2026 здесь стояло «ни одно поле не показывает ученику фамилию
 * другого ученика», и это больше не так.** Имя внутри группы перестало быть
 * обязательным, и незаданное разрешается в профильное, то есть возможно
 * показанное с фамилией. Продукт информирует и не блокирует: цену называет
 * предупреждение перед раскрытием состава.
 *
 * Что не изменилось: **профильное имя ученику не уезжает НИКОГДА в форме, где
 * преподаватель его не выбирал.** Список получателей у преподавателя и перечень
 * тех, кого рассылка не застала (`AnnouncementSkipped`), это его собственные
 * записи о его собственных учениках, и ребёнку они не показываются ни одним
 * путём.
 *
 * ⚠️ Имя ПРЕПОДАВАТЕЛЯ под это правило не подпадает и никогда не подпадало: оно
 * едет ученику подписью под каждым его сообщением
 * (`ConversationMessage.authorName`). Он не ребёнок, и своё имя ученику назвал
 * сам.
 *
 * ⚠️ **Схем ВЫДАЧИ здесь не осталось ни одной, и это правка 30.08.2026.**
 * Читается рассылка окном переписки, и формы для чтения живут в
 * `./conversations.ts`: `ConversationSummary` вместо строки ленты,
 * `ConversationMessage` вместо строки нитки. Здесь остались ключи, потолки,
 * срок хранения, разбор состояния сообщения и схемы ЗАПИСИ. Заводя тут новую
 * схему выдачи, сначала проверь, не про разговор ли она.
 *
 * ⚠️ Вместе с лентой и карточкой ушли `Announcement`, `AnnouncementCard`,
 * `AnnouncementListResponse`, `AnnouncementRecipient` и `AnnouncementReply`:
 * это были схемы снятых ручек, а не общий словарь. Потолки нитки
 * (`ANNOUNCEMENT_REPLIES_MAX`, `ANNOUNCEMENT_THREAD_FETCH_MAX`) ушли туда же,
 * потому что у страницы разговора потолок свой и с курсором
 * (`CONVERSATION_PAGE_LIMIT`).
 */

export const AnnouncementId = z.string().uuid();
export type AnnouncementId = z.infer<typeof AnnouncementId>;

export const AnnouncementReplyId = z.string().uuid();
export type AnnouncementReplyId = z.infer<typeof AnnouncementReplyId>;

/* ──────────────────────────── потолки ──────────────────────────── */

/**
 * Потолок поимённой адресации одного объявления.
 *
 * Число то же, что у рассылочного списка, и это намеренно, а не совпадение:
 * адресация группой и поимённая кладут ОДНИ И ТЕ ЖЕ строки получателей (раздел
 * 8 проекта), поэтому разные потолки означали бы, что один и тот же набор людей
 * адресуем одним способом и неадресуем другим.
 */
export const ANNOUNCEMENT_RECIPIENTS_MAX = MAILING_LIST_MEMBERS_MAX;

export const ANNOUNCEMENT_BODY_MAX = 2000;

/**
 * Ответ короче объявления вдвое, и это не симметрия ради симметрии: сообщения
 * едут страницей по дюжине штук, а отдача в режиме белого списка обрывается
 * около 16 КБ (раздел 3 CLAUDE.md).
 *
 * ⚠️ **Считать эту границу в ЗНАКАХ нельзя, и раньше здесь было посчитано
 * именно так.** Кириллица в UTF-8 это два байта на символ, то есть ответ на
 * тысячу знаков весит две тысячи байт. Отсюда же второй потолок,
 * `CONVERSATION_PAGE_LIMIT` в `./conversations.ts`: длины одного ответа мало,
 * пока число сообщений на странице не ограничено ничем.
 */
export const ANNOUNCEMENT_REPLY_BODY_MAX = 1000;

/**
 * Срок хранения СОДЕРЖИМОГО объявления и ответа (решение владельца 29.08.2026).
 * Один и тот же у обоих, и от группы он не зависит.
 *
 * ⚠️ Число живёт здесь, а не в уборщике, по общему правилу: срок, названный в
 * двух местах, однажды разъедется. Называет его и экран: слова про стёртое
 * сообщение собирает `erasedText` в
 * `apps/web/src/features/announcements/announcement-text.ts`, и число туда
 * приезжает отсюда, а не написано на глаз. По истечении срока стирается только
 * текст: факт объявления и квитанция остаются.
 */
export const ANNOUNCEMENT_RETENTION_DAYS = 30;

/* ─────────────────────────── тела и нитка ──────────────────────── */

/**
 * ⚠️ **`Printable` здесь обязателен, а не аккуратен.** Это свободный текст,
 * который пишут школьники и вставляют из PDF, а PostgreSQL не хранит нулевой
 * байт ни в `text`, ни в `jsonb`: вставка падает ошибкой драйвера, то есть
 * пятисоткой (разбор у самого `Printable` в `./feedback.ts`). Повторять
 * проверку третьим написанием регулярного выражения смысла нет: правило одно,
 * и разъехавшись, копии дали бы пятисотку ровно там, где вторая копия отстала.
 */
export const AnnouncementBody = Printable.trim().min(1).max(ANNOUNCEMENT_BODY_MAX);
export type AnnouncementBody = z.infer<typeof AnnouncementBody>;

export const AnnouncementReplyBody = Printable.trim().min(1).max(ANNOUNCEMENT_REPLY_BODY_MAX);
export type AnnouncementReplyBody = z.infer<typeof AnnouncementReplyBody>;

/**
 * Подпись ученика, которого назвать нечем, под его сообщением в окне переписки.
 *
 * ⚠️ **Случаев осталось два, и оба про отсутствие данных, а не про запрет**
 * (переписано 29.08.2026 вместе со снятием требования задавать имя). Первый:
 * автор ВЫШЕЛ из группы, строки участия нет, и задать ему имя преподаватель уже
 * не может. Второй: не задано ни имя в группе, ни профильное, то есть назвать
 * человека нечем ни одним полем.
 *
 * Участник без имени внутри группы сюда больше НЕ попадает: его показывают
 * профильным именем (`memberNameShown` в `./groups.ts`).
 *
 * Пустой строкой заглушка быть не может: подпись, которой нет, читается как
 * поломка экрана, а не как «имя не задано».
 */
export const ANNOUNCEMENT_UNKNOWN_AUTHOR = "Ученик";

/**
 * Состояний у ответа три, а полей под них два, и разбирать эту пару предстоит
 * каждому будущему рендереру.
 *
 * - `posted`: текст есть, показываем его
 * - `erased`: стёрт по сроку хранения (`ANNOUNCEMENT_RETENTION_DAYS`). Строка
 *   на месте, автор и время известны, текста больше нет ни у кого
 * - `deleted`: удалён человеком. Преподаватель убирает любой ответ, ученик свой
 *
 * ⚠️ Разница между двумя последними видна только человеку и только словами:
 * «сообщение удалено» и «срок хранения истёк» это разные события, и подпись у
 * них обязана быть разной. Поэтому функция, а не `body === null` в каждом
 * рендерере: то же правило, что у `homeworkState` и `memberNameShown`.
 */
export type AnnouncementReplyState = "posted" | "erased" | "deleted";

export function announcementReplyState(reply: {
  body: string | null;
  deletedAt: number | null;
}): AnnouncementReplyState {
  // Удаление старше срока: сначала смотрим на него, иначе стёртый по сроку
  // удалённый ответ показался бы как «истёк срок», хотя его убрали руками
  if (reply.deletedAt !== null) return "deleted";
  return reply.body === null ? "erased" : "posted";
}

/* ──────────────────────────── запись ───────────────────────────── */

/**
 * Создание объявления.
 *
 * Адресация ровно одна из двух: группой либо поимённым списком учеников. Оба
 * пути кладут одни и те же строки получателей, и различие исчезает сразу после
 * создания: `groupId` остаётся пометкой «как собрали», а не источником прав.
 *
 * ⚠️ **Взаимоисключение держит `refine`, а не комментарий.** Пока оба поля были
 * просто необязательными, объявление можно было адресовать поимённо И пометить
 * группой, и это не косметика: получатель, которого в группе нет, попадал в
 * нитку ВИДИМОЙ группы, где его подпись собирается по составу. Строки участия у
 * него там нет, то есть подписан он был бы заглушкой «Ученик» среди детей,
 * которых преподаватель собирал осознанно, а `groupTitle` и состав чужой группы
 * читались бы раньше, чем сработает отсечка составного FK на вставке
 * получателя.
 *
 * ⚠️ Пустая адресация это отказ `announcement_empty`, а не `invalid_body`, и
 * `refine` её намеренно пропускает: «ни группы, ни списка» и «группа оказалась
 * пустой» это для человека одно и то же событие, и отвечать на них разными
 * кодами значит требовать от экрана различать неразличимое. Истина это список
 * получателей, и объявление, которого никто не получил, не означает ничего.
 */
export const CreateAnnouncementRequest = z
  .object({
    groupId: GroupId.optional(),
    /** Поимённая адресация. Индивидуальное объявление это список из одного. */
    studentUserIds: z.array(UserId).min(1).max(ANNOUNCEMENT_RECIPIENTS_MAX).optional(),
    body: AnnouncementBody,
  })
  .refine((v) => v.groupId === undefined || v.studentUserIds === undefined, {
    message: "адресация ровно одна: либо группа, либо поимённый список",
    path: ["groupId"],
  });
export type CreateAnnouncementRequest = z.infer<typeof CreateAnnouncementRequest>;

/**
 * Кого объявление не застало и почему.
 *
 * ⚠️ **Молчаливой деградации у рассылки нет** (раздел 5.5 CLAUDE.md). Названного
 * поимённо отвязанного ученика ручка отвергает целиком (`409
 * student_not_linked`): преподаватель выбрал именно его. Участник группы, с
 * которым пара отвязана, рассылку не останавливает, но и не исчезает молча:
 * группа это «как собрали список», а «отправлено восьмерым» вместо десяти без
 * единого слова о двоих это ровно то, что запрещено.
 *
 * Причина пока одна, и перечислением, а не строкой: следующая («заблокирован»)
 * приедет отдельным значением, а не новым полем.
 */
export const AnnouncementSkipped = z.object({
  studentUserId: UserId,
  /**
   * Имя ученика из списка ПРЕПОДАВАТЕЛЯ (`app_user.display_name`).
   *
   * ⚠️ Профильное имя здесь преподавательское насквозь: строка едет в ответе на
   * СОБСТВЕННУЮ отправку, ребёнку не показывается ни одним путём, и человек в
   * ней это ученик, которого преподаватель завёл сам. Каким именем ученик видит
   * ДРУГОГО ученика, решается не здесь, а `authorNameFor` на сервере.
   *
   * ⚠️ Заведено 29.08.2026: раньше ехал один идентификатор, а имя экран искал
   * вторым запросом в списке учеников. Не доехал список (сеть в наших условиях
   * рвётся штатно) значит вместо имён на экране оставалось «ещё 2», то есть
   * человек узнавал, что кому-то не ушло, и не узнавал кому.
   */
  studentName: z.string(),
  reason: z.enum(["unlinked"]),
});
export type AnnouncementSkipped = z.infer<typeof AnnouncementSkipped>;

/**
 * Ответ на создание объявления.
 *
 * Схемой, а не формой, собранной в маршруте: два интерфейса вместо одной схемы
 * zod это то, от чего заведён `@edu/shared` (раздел 0 CLAUDE.md), и для ответа
 * это верно ровно так же, как для запроса.
 */
export const CreateAnnouncementResponse = z.object({
  id: AnnouncementId,
  /** Сколько человек получили. Всегда больше нуля: пустая рассылка это отказ. */
  recipients: z.number().int().positive(),
  /** Кого пропустили. Пустой массив это норма, а не отсутствие поля. */
  skipped: z.array(AnnouncementSkipped),
});
export type CreateAnnouncementResponse = z.infer<typeof CreateAnnouncementResponse>;

export const CreateAnnouncementReplyResponse = z.object({
  id: AnnouncementReplyId,
  createdAt: z.number().int(),
});
export type CreateAnnouncementReplyResponse = z.infer<typeof CreateAnnouncementReplyResponse>;

export const CreateAnnouncementReplyRequest = z.object({
  body: AnnouncementReplyBody,
});
export type CreateAnnouncementReplyRequest = z.infer<typeof CreateAnnouncementReplyRequest>;
